// lodash utils
// api utils
import { postRequest } from '../api';
import {
  orderDetailsRequest,
  instoreOrderDetailsRequest,
} from '../api/requestCreator';
// selectors
import { getInputValue } from '../reducers/orderTracking';
// analytics
import { setErrorEvent } from '../analytics/gatherService';
// contants
import { ROUTES, ACTIONS, REDUCERS } from '../constants';
// other utils
import {
  handleOnlineOrderError,
  handleInStoreOrderError,
} from '../utils/errors';
import { getIsUserLoggedIn } from '../utils/cookie';
import { redirectToPath } from '../utils/window';
import { formatOrderDetailsPayload } from '../utils/format';
import { validInput } from './common';
import { isInstoreOrder } from '../utils/validations';

/**
 * function that gets called on api success response
 * @param {*} dispatch
 * @param {*} response
 * @param {*} history
 * @param {*} request
 */
const onSuccess = (
  dispatch: Function,
  response: Object,
  history: Object,
  request: Object
) => {
  // check for instore order
  // redirect to angular app
  const { isInstore } = request;
  const isAuth = getIsUserLoggedIn();

  let pathname = '';
  if (isInstore) {
    pathname = isAuth
      ? ROUTES.PATH.AUTH_IN_STORE_ORDER
      : ROUTES.PATH.IN_STORE_ORDER;
  } else {
    pathname = ROUTES.PATH.ONLINE_ORDER;
  }

  // online order
  const { orderDetails } = response.data;
  const { orderGroups } = orderDetails;

  if (!orderGroups) {
    localStorage.setItem(
      'currentOrderDetails',
      JSON.stringify({ orderDetails })
    );
    console.log('No OrderGroups Found, rerouting');
    redirectToPath(ROUTES.URL.GUEST_ORDER_DETAILS_V1);
    return;
  }
  const { response: order, lineItems } = formatOrderDetailsPayload(
    response.data,
    isInstore
  );
  dispatch({
    type: ACTIONS.FETCH_ORDER_DETAILS_SUCCESS,
    response: order,
    lineItems,
    filter: REDUCERS.ORDER_DETAILS,
  });

  // on success
  // navigate to order details page
  history.push(pathname);
};

/**
 * function that gets called on api error response
 * @param {*} dispatch
 * @param {*} error
 * @param {*} request
 */
const onFailure = (dispatch: Function, error: Object, request: Object) => {
  const { isInstore, isEmailLink, orderId } = request;
  if (isEmailLink) {
    validInput(dispatch, 'order', orderId);
    dispatch({
      type: ACTIONS.FETCH_ORDER_DETAILS_EMAIL_FAILURE,
      filter: REDUCERS.ORDER_DETAILS,
    });
    return;
  }

  const { errorData } = error.response.data;
  const errorMessage = errorData
    ? errorData.errors.error
    : [{ code: 'OS_COMORDERDETAILS_ERR_2000' }];

  dispatch({
    type: ACTIONS.FETCH_ORDER_DETAILS_FAILURE,
    message: errorMessage,
    filter: REDUCERS.ORDER_DETAILS,
  });

  // analytics error event
  const errorCode = errorMessage[0].code;
  const errorMessageFormatted = isInstore
    ? handleInStoreOrderError(errorCode)
    : handleOnlineOrderError(errorCode);
  const pageSection = 'order tracking form';
  const errorType = isInstore ? 'error' : 'system';
  setErrorEvent(errorMessageFormatted, pageSection, errorCode, errorType);
};

/**
 * middleware function that gets called directly from react component
 * dispatch action to trigger loading indicator
 * make api call
 * dispatch action based on api response
 * @param {*} history
 * @param {*} request - optional: { isInstore: false, isEmailLink: false }
 */
export const fetchOrderDetails = (
  history: Object,
  request: Object = {
    isInstore: false,
    isEmailLink: false,
    orderId: null,
    emailId: null,
  }
) => (dispatch: Function, getState: Function) => {
  const { isInstore, orderId, emailId } = request;
  const order =
    orderId ||
    getInputValue(getState(), 'order') ||
    getInputValue(getState(), REDUCERS.AUTH_INSTORE);
  const email = emailId || getInputValue(getState(), 'email');
  const phone = getInputValue(getState(), 'phone');
  const payload = isInstore
    ? instoreOrderDetailsRequest(order, phone)
    : orderDetailsRequest(order, email);

  // dispatch loading indicator before sending request
  dispatch({
    type: ACTIONS.FETCH_ORDER_DETAILS,
    filter: REDUCERS.ORDER_DETAILS,
  });

  const { url, headers, data } = payload;
  postRequest(url, data, headers).then(
    (response: Object) => {
      onSuccess(dispatch, response, history, request);
    },
    (error: Object) => {
      onFailure(dispatch, error, request);
    }
  );
};

// this action may be called from promise.then() in order to update the order details if the status changes
export const getOrderDetails = (
  // TODO: validate this method
  id: string,
  emailId: string,
  phone: string
) => (dispatch: Function) => {
  dispatch({
    type: ACTIONS.FETCH_ORDER_DETAILS,
    filter: REDUCERS.ORDER_DETAILS,
  });

  const isInstore = isInstoreOrder(id);
  const { url, headers, data } = isInstore
    ? instoreOrderDetailsRequest(id, phone)
    : orderDetailsRequest(id, emailId);

  postRequest(url, data, headers).then(
    (response: Object) => {
      const { response: order, lineItems } = formatOrderDetailsPayload(
        response.data
      );
      dispatch({
        type: ACTIONS.FETCH_ORDER_DETAILS_SUCCESS,
        response: order,
        lineItems,
        filter: REDUCERS.ORDER_DETAILS,
      });
    },
    (error: Object) => {
      console.log(error.response);
      dispatch({
        type: ACTIONS.FETCH_ORDER_DETAILS_FAILURE,
        filter: REDUCERS.ORDER_DETAILS,
        message: `We're sorry! A temporary connection error prevented your request from going through. Please try again.`,
      });
    }
  );
};
